// Web → Analytika → Export.
//
// Stáhne kompletní naměřená čísla za vybrané období do souboru. Nic nového
// nepočítá, jen si znovu zavolá /api/web-analytics a to, co přijde, uloží
// na disk. Proto tu nemůže vzniknout jiné číslo, než jaké je vidět na
// obrazovce.
//
// Dva formáty, každý k něčemu jinému:
//   JSON — úplně všechno včetně sekcí, cest a kroků kvízu. Tohle pošlete,
//          když chcete čísla někomu dát k rozboru.
//   CSV  — hlavní tabulky pod sebou, otevře se v Excelu. Oddělovač je
//          středník, protože český Excel s čárkou hodí všechno do jednoho
//          sloupce.
//
// Období „vše" sahá k 1. 1. 2024, tedy hluboko před spuštění měření. Starší
// data neexistují, takže to spolehlivě znamená „od začátku".
import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Download, X, FileJson, FileSpreadsheet } from "lucide-react";
import { fetchApi } from "../../lib/api.js";

const ZACATEK_VSEHO = "2024-01-01";

const dnesISO = () => new Date().toISOString().slice(0, 10);
const predDny = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const OBDOBI = [
  { k: "7", label: "7 dní" },
  { k: "30", label: "30 dní" },
  { k: "90", label: "90 dní" },
  { k: "365", label: "Rok" },
  { k: "vse", label: "Vše" },
  { k: "vlastni", label: "Vlastní" },
];

// Bloky, které se dostanou do CSV, v pořadí, v jakém dávají smysl při čtení.
// Co v odpovědi není, se přeskočí, takže tenhle seznam nemusí být přesný.
const CSV_TABULKY = [
  ["Zdroje návštěvnosti", "zdroje"],
  ["Návštěvnost po dnech", "denne"],
  ["Jednotlivé stránky", "stranky"],
  ["Varianty prodejní cesty", "varianty"],
  ["Kroky kvízu", "kvizKroky"],
  ["Kde kvíz opustily", "kvizOdchody"],
  ["Sekce prodejní stránky", "sekceProdejka"],
  ["Sekce nákupní stránky", "sekceNakup"],
  ["Odchody z prodejní stránky", "odchodyProdejka"],
  ["Odchody z nákupní stránky", "odchodyNakup"],
  ["Zařízení", "zarizeni"],
  ["Typy výsledku kvízu", "kvizTypy"],
  ["Přechody mezi stránkami", "cesty"],
  ["Tok včetně odchodů", "tok"],
  ["Čas na stránkách", "casy"],
  ["Kliknutí k objednávce", "klikyCelkem"],
  ["Tlačítka v kalkulátoru", "akce"],
  ["Vstupní stránky", "vstupy"],
  ["Okno tajné slevy", "slevaCas"],
];

const CSV_SOUHRNY = [
  ["Souhrn", "souhrn"],
  ["Chování", "chovani"],
  ["Trychtýř", "funnel"],
  ["Čas celkem", "casSouhrn"],
];

const bunka = (v) => {
  if (v == null) return "";
  const s = typeof v === "object" ? JSON.stringify(v) : String(v);
  return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function tabulkaDoCsv(nazev, radky) {
  if (!Array.isArray(radky) || !radky.length) return [];
  const sloupce = [];
  for (const r of radky) {
    for (const k of Object.keys(r || {})) if (!sloupce.includes(k)) sloupce.push(k);
  }
  const out = [`# ${nazev}`, sloupce.join(";")];
  for (const r of radky) out.push(sloupce.map((c) => bunka(r?.[c])).join(";"));
  out.push("");
  return out;
}

function objektDoCsv(nazev, o) {
  if (!o || typeof o !== "object") return [];
  const klice = Object.keys(o);
  if (!klice.length) return [];
  const out = [`# ${nazev}`, "ukazatel;hodnota"];
  for (const k of klice) out.push(`${bunka(k)};${bunka(o[k])}`);
  out.push("");
  return out;
}

function stahni(obsah, nazev, typ) {
  const blob = new Blob([obsah], { type: typ });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nazev;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// 14. 9. 2026 (fáze 6): okno jde otevřít i zvenku (z nabídky v hlavičce).
// Když stránka předá `open` a `onClose`, tlačítko se nekreslí a stav řídí ona.
export default function ExportStatistik({ open, onClose }) {
  const rizene = typeof open === "boolean";
  const [vlastniOtevreno, setOtevreno] = useState(false);
  const otevreno = rizene ? open : vlastniOtevreno;
  const [obdobi, setObdobi] = useState("30");
  const [od, setOd] = useState(predDny(30));
  const [doD, setDoD] = useState(dnesISO());
  const [json, setJson] = useState(true);
  const [csv, setCsv] = useState(true);
  const [bezi, setBezi] = useState(false);
  const [hlaska, setHlaska] = useState(null);

  const rozsah = () => {
    if (obdobi === "vse") return { od: ZACATEK_VSEHO, do: dnesISO() };
    if (obdobi === "vlastni") return { od, do: doD };
    return { od: predDny(Number(obdobi)), do: dnesISO() };
  };

  const exportuj = async () => {
    setBezi(true);
    setHlaska(null);
    try {
      const r = rozsah();
      if (!r.od || !r.do || r.od > r.do) {
        setHlaska({ typ: "chyba", text: "Datum od musí být dřív než datum do." });
        setBezi(false);
        return;
      }
      const data = await fetchApi(`/api/web-analytics?od=${r.od}&do=${r.do}`);
      const znacka = `${r.od}_${r.do}`;

      if (json) {
        const balik = {
          web: "fitway40.cz",
          obdobi: { od: r.od, do: r.do },
          vyexportovano: new Date().toISOString(),
          poznamka:
            "Vlastní měření bez cookies. Návštěvnice se rozlišují otiskem, který se každý den mění, " +
            "takže stejný člověk se napříč dny počítá znovu a spojení mezi dny nejde dohledat.",
          data,
        };
        stahni(JSON.stringify(balik, null, 2), `fitway40-analytika-${znacka}.json`, "application/json");
      }

      if (csv) {
        let radky = [`# FitWay40, analytika webu`, `# období;${r.od};${r.do}`, ""];
        for (const [nazev, klic] of CSV_SOUHRNY) radky = radky.concat(objektDoCsv(nazev, data?.[klic]));
        for (const [nazev, klic] of CSV_TABULKY) radky = radky.concat(tabulkaDoCsv(nazev, data?.[klic]));
        // BOM, jinak Excel na Windows rozbije háčky a čárky.
        stahni("﻿" + radky.join("\n"), `fitway40-analytika-${znacka}.csv`, "text/csv;charset=utf-8");
      }

      const kolik = (json ? 1 : 0) + (csv ? 1 : 0);
      setHlaska({
        typ: "ok",
        text: kolik === 2 ? "Staženy dva soubory, JSON i CSV." : "Soubor stažen.",
      });
    } catch (e) {
      setHlaska({ typ: "chyba", text: e.message || "Export se nepovedl." });
    } finally {
      setBezi(false);
    }
  };

  const zavri = () => {
    if (bezi) return;
    if (rizene) onClose?.();
    else setOtevreno(false);
    setHlaska(null);
  };

  return (
    <>
      {!rizene && (
        <button
          type="button"
          onClick={() => setOtevreno(true)}
          title="Stáhnout kompletní statistiky"
          className="inline-flex items-center gap-1.5 rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-sm font-medium text-white/90 transition hover:bg-white/20"
        >
          <Download className="h-4 w-4" /> Exportovat
        </button>
      )}

      {otevreno &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-brand-dark/50 p-4 backdrop-blur-sm pb-[max(1rem,env(safe-area-inset-bottom))]"
            onClick={(e) => {
              if (e.target === e.currentTarget) zavri();
            }}
          >
            <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="mb-5 flex items-start gap-4">
                <div className="flex-shrink-0 rounded-xl bg-brand-100 p-3 text-brand-700">
                  <Download className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-semibold leading-tight text-slate-900">Export statistik webu</h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">
                    Stáhne všechna naměřená čísla za vybrané období. Nic se nikam neposílá, soubor se uloží
                    rovnou k vám do počítače.
                  </p>
                </div>
                <button
                  onClick={zavri}
                  className="text-slate-400 transition hover:text-slate-600"
                  aria-label="Zavřít"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* --------------------------------------------------- období */}
              <div className="mb-2 text-sm font-medium text-slate-700">Období</div>
              <div className="mb-4 flex flex-wrap gap-1.5">
                {OBDOBI.map((o) => (
                  <button
                    key={o.k}
                    type="button"
                    onClick={() => setObdobi(o.k)}
                    className={`rounded-xl px-3 py-1.5 text-sm font-medium transition ${
                      obdobi === o.k
                        ? "bg-brand-600 text-white shadow-sm"
                        : "border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>

              {obdobi === "vlastni" && (
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    Od
                    <input
                      type="date"
                      value={od}
                      max={doD}
                      onChange={(e) => setOd(e.target.value)}
                      className="field w-auto"
                    />
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    Do
                    <input
                      type="date"
                      value={doD}
                      min={od}
                      max={dnesISO()}
                      onChange={(e) => setDoD(e.target.value)}
                      className="field w-auto"
                    />
                  </label>
                </div>
              )}

              {/* --------------------------------------------------- formáty */}
              <div className="mb-2 text-sm font-medium text-slate-700">Co stáhnout</div>
              <div className="mb-5 space-y-2">
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-3 transition hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={json}
                    onChange={(e) => setJson(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-600"
                  />
                  <FileJson className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
                  <span className="text-sm">
                    <span className="font-medium text-slate-900">JSON, úplně všechno</span>
                    <span className="block text-slate-500">
                      Včetně sekcí, cest a kroků kvízu. Tenhle soubor pošlete, když chcete čísla dát někomu k
                      rozboru.
                    </span>
                  </span>
                </label>

                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-3 transition hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={csv}
                    onChange={(e) => setCsv(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-600"
                  />
                  <FileSpreadsheet className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                  <span className="text-sm">
                    <span className="font-medium text-slate-900">CSV pro Excel</span>
                    <span className="block text-slate-500">
                      Hlavní tabulky pod sebou, každá s vlastní hlavičkou. Otevře se dvojklikem.
                    </span>
                  </span>
                </label>
              </div>

              {hlaska && (
                <div
                  className={`mb-4 rounded-xl border p-3 text-sm ${
                    hlaska.typ === "ok"
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                      : "border-rose-200 bg-rose-50 text-rose-700"
                  }`}
                >
                  {hlaska.text}
                </div>
              )}

              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={zavri}
                  disabled={bezi}
                  className="btn btn-secondary"
                >
                  Zavřít
                </button>
                <button
                  onClick={exportuj}
                  disabled={bezi || (!json && !csv)}
                  className="btn btn-primary"
                >
                  {bezi && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />}
                  {bezi ? "Připravuji…" : "Stáhnout"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
